import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";import { toast } from "sonner";
import { Plus, Trash2, Save, X, Briefcase, ArrowUp, ArrowDown, ArrowLeft, ExternalLink, Eye, Building2, Phone, Mail, MapPin, Globe, ChevronDown } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Field, ImageUpload } from "../../components/admin/Fields";
import BlockBuilder from "../../components/admin/BlockBuilder";
import FocusPointPicker from "../../components/admin/FocusPointPicker";
import RichEditor from "../../components/admin/RichEditor";
import { PageTitle } from "./Layout";

const EMPTY = () => ({
  slug: "", is_published: true, order: 0, icon: "Briefcase", cover: "", cover_pos_x: 50, cover_pos_y: 50, hero_height: 0,
  title: { tr: "", en: "" }, card_title: { tr: "", en: "" }, short: { tr: "", en: "" }, description: { tr: "", en: "" },
  highlights: [], cases: [], testimonials: [], content_blocks: [],
  company: { name: "", logo: "", address: "", phone: "", email: "", website: "", map_url: "", map_embed: "" },
  cta_label: { tr: "İletişime Geç", en: "Get in Touch" }, cta_target: "/iletisim",
});

const LUCIDE_SUGGESTIONS = [
  "Briefcase", "Scale", "GraduationCap", "Users", "Megaphone", "BookOpen",
  "Gavel", "Target", "Building2", "HeartHandshake", "Lightbulb", "Globe2",
  "Mic", "FileSignature", "Compass", "Trophy", "ShieldCheck", "Rocket",
];

function I18nInput({ value, onChange, placeholder, testid, lang = "tr", textarea = false }) {
  const Cmp = textarea ? Textarea : Input;
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      <Cmp value={value?.tr || ""} onChange={(e) => onChange({ ...(value || {}), tr: e.target.value })} placeholder={`${placeholder} (TR)`} data-testid={`${testid}-tr`} rows={textarea ? 3 : undefined} />
      <Cmp value={value?.en || ""} onChange={(e) => onChange({ ...(value || {}), en: e.target.value })} placeholder={`${placeholder} (EN)`} data-testid={`${testid}-en`} rows={textarea ? 3 : undefined} />
    </div>
  );
}

function CollapsibleCard({ num, title, subtitle, icon = null, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (!testid) return;
    const onOpen = (e) => { if (e.detail?.id === testid) setOpen(true); };
    window.addEventListener("admin:open-section", onOpen);
    return () => window.removeEventListener("admin:open-section", onOpen);
  }, [testid]);
  return (
    <section className="bg-white border rounded-xl shadow-sm overflow-hidden transition-colors" data-testid={testid}>
      <header
        className="px-6 py-4 border-b bg-stone-50/60 flex items-center gap-3 cursor-pointer hover:bg-stone-100/70 transition select-none"
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">{num}</div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold flex items-center gap-2">{icon}{title}</h3>
          {subtitle && <p className="text-xs text-stone-500">{subtitle}</p>}
        </div>
        <ChevronDown size={18} className={`text-stone-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </header>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="p-6">{children}</div>
        </div>
      </div>
    </section>
  );
}

function Highlights({ items, onChange }) {
  const [v, setV] = useState("");
  const add = () => { const x = v.trim(); if (!x) return; onChange([...(items || []), x]); setV(""); };
  return (
    <div className="space-y-2">
      {(items || []).map((h, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={h} onChange={(e) => { const nx = [...items]; nx[i] = e.target.value; onChange(nx); }} data-testid={`service-highlight-${i}`} />
          <Button type="button" variant="ghost" size="sm" onClick={() => { const nx = [...items]; nx.splice(i, 1); onChange(nx); }}><X size={14} /></Button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="Yeni öne çıkan başlık (örn. Stratejik planlama)" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="service-highlight-new" />
        <Button type="button" size="sm" onClick={add}><Plus size={14} /></Button>
      </div>
    </div>
  );
}

function CaseEditor({ items, onChange }) {
  const add = () => onChange([...(items || []), { title: { tr: "", en: "" }, summary: { tr: "", en: "" }, year: "", cover: "", external_url: "" }]);
  const upd = (i, patch) => { const nx = [...items]; nx[i] = { ...nx[i], ...patch }; onChange(nx); };
  const del = (i) => { const nx = [...items]; nx.splice(i, 1); onChange(nx); };
  return (
    <div className="space-y-4">
      {(items || []).map((c, i) => (
        <div key={c.id || i} className="border rounded-lg p-4 bg-stone-50 space-y-3" data-testid={`service-case-${i}`}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Çalışma #{i + 1}</p>
            <Button type="button" variant="ghost" size="sm" onClick={() => del(i)}><Trash2 size={14} className="text-red-600" /></Button>
          </div>
          <Field label="Başlık (TR / EN)"><I18nInput value={c.title} onChange={(v) => upd(i, { title: v })} placeholder="Başlık" testid={`case-${i}-title`} /></Field>
          <Field label="Özet (TR / EN)"><I18nInput value={c.summary} onChange={(v) => upd(i, { summary: v })} placeholder="Kısa özet" testid={`case-${i}-summary`} textarea /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Yıl"><Input value={c.year || ""} onChange={(e) => upd(i, { year: e.target.value })} placeholder="2024" data-testid={`case-${i}-year`} /></Field>
            <Field label="Dış bağlantı (opsiyonel)"><Input value={c.external_url || ""} onChange={(e) => upd(i, { external_url: e.target.value })} placeholder="https://…" data-testid={`case-${i}-url`} /></Field>
          </div>
          <ImageUpload label="Kapak görseli" value={c.cover} onChange={(v) => upd(i, { cover: v })} testid={`case-${i}-cover`} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="service-case-add"><Plus size={14} className="mr-1" />Yeni çalışma ekle</Button>
    </div>
  );
}

function TestimonialEditor({ items, onChange }) {
  const add = () => onChange([...(items || []), { name: "", role: "", company: "", logo: "", text: { tr: "", en: "" } }]);
  const upd = (i, patch) => { const nx = [...items]; nx[i] = { ...nx[i], ...patch }; onChange(nx); };
  const del = (i) => { const nx = [...items]; nx.splice(i, 1); onChange(nx); };
  return (
    <div className="space-y-4">
      {(items || []).map((q, i) => (
        <div key={q.id || i} className="border rounded-lg p-4 bg-stone-50 space-y-3" data-testid={`service-ref-${i}`}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Referans #{i + 1}</p>
            <Button type="button" variant="ghost" size="sm" onClick={() => del(i)}><Trash2 size={14} className="text-red-600" /></Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Ad Soyad"><Input value={q.name || ""} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`ref-${i}-name`} /></Field>
            <Field label="Rol"><Input value={q.role || ""} onChange={(e) => upd(i, { role: e.target.value })} data-testid={`ref-${i}-role`} /></Field>
            <Field label="Kurum"><Input value={q.company || ""} onChange={(e) => upd(i, { company: e.target.value })} data-testid={`ref-${i}-company`} /></Field>
          </div>
          <Field label="Metin (TR / EN)"><I18nInput value={q.text} onChange={(v) => upd(i, { text: v })} placeholder="Referans yazısı" testid={`ref-${i}-text`} textarea /></Field>
          <ImageUpload label="Logo / profil fotoğrafı (opsiyonel)" value={q.logo} onChange={(v) => upd(i, { logo: v })} testid={`ref-${i}-logo`} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="service-ref-add"><Plus size={14} className="mr-1" />Yeni referans ekle</Button>
    </div>
  );
}

function CompanyEditor({ value, onChange }) {
  const v = value || {};
  const set = (k) => (e) => onChange({ ...v, [k]: typeof e === "string" ? e : e.target.value });
  const setVal = (k) => (val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-4" data-testid="service-company">
      <div className="bg-amber-50/60 border border-amber-200/60 rounded-lg p-3 text-xs text-stone-700 flex items-start gap-2">
        <Building2 size={14} className="shrink-0 mt-0.5 text-amber-700" />
        <span>Bu hizmeti verdiğiniz firma bilgileri — hizmet detay sayfasında bir "Firma kartı" olarak gösterilir. Boş bıraktığınız alanlar görünmez.</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Firma adı"><Input value={v.name || ""} onChange={set("name")} placeholder="FixNet Broadband Ltd." data-testid="company-name" /></Field>
        <Field label="Web sitesi"><Input value={v.website || ""} onChange={set("website")} placeholder="https://firma.com" data-testid="company-website" /></Field>
      </div>
      <ImageUpload label="Firma logosu" value={v.logo} onChange={setVal("logo")} testid="company-logo" />
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Telefon"><Input value={v.phone || ""} onChange={set("phone")} placeholder="+90 392 ..." data-testid="company-phone" /></Field>
        <Field label="E-posta"><Input value={v.email || ""} onChange={set("email")} placeholder="info@firma.com" data-testid="company-email" /></Field>
      </div>
      <Field label="Adres"><Textarea value={v.address || ""} onChange={set("address")} placeholder="Mahalle, cadde, no, şehir" rows={2} data-testid="company-address" /></Field>
      <Field label="Google Haritalar bağlantısı" hint="Haritalarda yeri paylaş butonundan aldığın normal link (örn. https://maps.app.goo.gl/...). Sayfada bir 'Haritada aç' butonu olarak görünür.">
        <Input value={v.map_url || ""} onChange={set("map_url")} placeholder="https://maps.app.goo.gl/..." data-testid="company-map-url" />
      </Field>
      <Field label="Google Haritalar gömme (iframe src)" hint="Haritalarda 'Paylaş > Harita yerleştir > HTML'i kopyala'dan aldığın iframe'in src değerini yapıştır. Boş bırakırsan harita gömülmez, sadece buton olur.">
        <Textarea value={v.map_embed || ""} onChange={set("map_embed")} placeholder="https://www.google.com/maps/embed?pb=..." rows={2} data-testid="company-map-embed" />
      </Field>
    </div>
  );
}

// Map a Pydantic field path (e.g. "description.tr", "company.email", "content_blocks.2.data.html")
// to the admin collapsible section that contains the input.
const FIELD_TO_SECTION = {
  title: "section-service-title",
  card_title: "section-service-title",
  description: "section-description",
  icon: "section-visual",
  slug: "section-visual",
  cover: "section-visual",
  cover_pos_x: "section-visual",
  cover_pos_y: "section-visual",
  hero_height: "section-visual",
  highlights: "section-highlights",
  cta_label: "section-highlights",
  cta_target: "section-highlights",
  company: "section-company",
  content_blocks: "section-blocks",
  cases: "section-cases",
  testimonials: "section-references",
};

function highlightFields(fields) {
  if (!Array.isArray(fields) || !fields.length) return;
  const seen = new Set();
  const sectionIds = fields
    .map((f) => (f?.field || "").split(".")[0])
    .map((root) => FIELD_TO_SECTION[root])
    .filter((id) => id && !seen.has(id) && seen.add(id));
  // Open each section
  sectionIds.forEach((id) => {
    window.dispatchEvent(new CustomEvent("admin:open-section", { detail: { id } }));
  });
  // Scroll + flash the first one after it has had a chance to expand
  const first = sectionIds[0];
  if (!first) return;
  setTimeout(() => {
    const el = document.querySelector(`[data-testid="${first}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("admin-err-flash");
    setTimeout(() => el.classList.remove("admin-err-flash"), 2600);
  }, 380);
}

function Editor({ initial, onCancel, onSaved }) {
  const qc = useQueryClient();
  const [f, setF] = useState(initial || EMPTY());
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  useEffect(() => { setF(initial || EMPTY()); }, [initial]);

  const openPreview = () => {
    if (!f.slug) { toast.info("Önce kaydet, sonra önizleme açabilirsin."); return; }
    window.open(`/admin/calismalarim/onizleme/${f.slug}`, "_blank", "noopener");
  };

  const save = async () => {
    if (!(f.title?.tr || "").trim()) { toast.error("Türkçe başlık zorunludur"); return; }
    setBusy(true);
    try {
      const payload = { ...f };
      let saved;
      if (f.id) {
        const r = await api.put(`/admin/services/${f.id}`, payload);
        saved = r.data;
        toast.success("Hizmet güncellendi");
      } else {
        const r = await api.post("/admin/services", payload);
        saved = r.data;
        toast.success("Hizmet eklendi");
      }
      // Stay on the editor — merge back the saved record (new id/slug/etc.)
      if (saved && typeof saved === "object") setF(saved);
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
      // Notify parent so background list refreshes, but we DO NOT leave the editor.
      if (typeof onSaved === "function") onSaved({ stay: true });
    } catch (e) {
      toast.error(errMsg(e));
      const fields = e?.response?.data?.fields || [];
      if (fields.length) highlightFields(fields);
    }
    finally { setBusy(false); }
  };

  const Toolbar = ({ bottom = false }) => (
    <div
      className={
        bottom
          ? "sticky bottom-0 z-20 -mx-4 sm:mx-0 bg-white/95 backdrop-blur border-t sm:border sm:rounded-xl px-4 sm:px-5 py-3 flex items-center justify-between gap-3 flex-wrap shadow-[0_-6px_18px_-10px_rgba(0,0,0,0.15)]"
          : "sticky top-16 z-20 -mx-4 sm:mx-0 bg-white/95 backdrop-blur border-b sm:border sm:rounded-xl px-4 sm:px-5 py-3 flex items-center justify-between gap-3 flex-wrap"
      }
      data-testid={bottom ? "service-editor-toolbar-bottom" : "service-editor-toolbar-top"}
    >
      <h2 className="font-semibold text-lg">{f.id ? "Hizmeti düzenle" : "Yeni hizmet"}</h2>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={!!f.is_published} onCheckedChange={set("is_published")} data-testid={`service-published-toggle${bottom ? "-bottom" : ""}`} />
          Yayında
        </label>
        <Button type="button" variant="outline" size="sm" onClick={openPreview} disabled={!f.slug} data-testid={`service-preview-open${bottom ? "-bottom" : ""}`}>
          <Eye size={14} className="mr-1" />Canlı önizleme
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid={`service-back${bottom ? "-bottom" : ""}`}>
          <ArrowLeft size={14} className="mr-1" />Önceki sayfa
        </Button>
        <Button type="button" onClick={save} disabled={busy} data-testid={`service-save${bottom ? "-bottom" : ""}`}>
          <Save size={14} className="mr-1" />{busy ? "Kaydediliyor…" : "Kaydet"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="service-editor">
      {/* Top sticky toolbar */}
      <Toolbar />

      <div className="space-y-6 min-w-0">
          {/* Help banner */}
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-xs text-stone-600 flex items-start gap-2.5">
            <span className="shrink-0 mt-0.5">💡</span>
            <span><strong>Dinamik sayfa düzeni:</strong> Üstte temel bilgiler, altında WordPress mantığıyla <strong>bloklar</strong> ile sayfayı istediğin gibi kuruyorsun. Kaydettikten sonra sağ üstteki <strong>"Canlı önizleme"</strong> butonu ile ayrı sekmede gerçek görünümü aç.</span>
          </div>

          {/* --- 1. Hizmet başlığı --- */}
          <CollapsibleCard num={1} title="Hizmet başlığı" subtitle='Detay sayfasında büyük başlık olarak gösterilir. İsteğe bağlı olarak alt satırda liste kartı için ayrı bir "kart başlığı" tanımlayabilirsin.' defaultOpen testid="section-service-title">
            <div className="space-y-5">
              <Field label="Detay sayfası başlığı (TR / EN)">
                <I18nInput value={f.title} onChange={set("title")} placeholder="Hukuki Danışmanlık" testid="service-title" />
              </Field>
              <Field label="Kart başlığı (opsiyonel)" hint="Çalışmalarım listesinde ve kartlarda bu başlık gösterilir. Boş bırakırsan yukarıdaki detay başlığı kullanılır.">
                <I18nInput value={f.card_title} onChange={set("card_title")} placeholder="Kısa kart başlığı" testid="service-card-title" />
              </Field>
            </div>
          </CollapsibleCard>

          {/* --- 2. Uzun açıklama (RichEditor) --- */}
          <CollapsibleCard num={2} title="Uzun açıklama" subtitle="Detay sayfasının ana metni. Zengin editörle başlık, kalın, listeler, bağlantı ekleyebilirsin." defaultOpen testid="section-description">
            <RichEditor value={f.description || {}} onChange={set("description")} testid="service-description" minH="min-h-[220px]" />
          </CollapsibleCard>

          {/* --- 3. Görsel kimlik: ikon + slug + kapak + odak --- */}
          <CollapsibleCard num={3} title="Görsel kimlik" subtitle="Kart ikonu, URL ve detay sayfasının kapak görseli." testid="section-visual">
            <div className="space-y-6">
              {/* Icon */}
              <Field label="İkon" hint="Çalışmalarım listesinde ve altbilgide bu ikon görünür.">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center">
                    <Briefcase size={22} />
                  </div>
                  <Input value={f.icon || ""} onChange={(e) => set("icon")(e.target.value)} data-testid="service-icon" className="max-w-xs" placeholder="Briefcase" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {LUCIDE_SUGGESTIONS.map((n) => (
                    <button key={n} type="button" onClick={() => set("icon")(n)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${f.icon === n ? "bg-s-primary text-white border-s-primary" : "bg-stone-50 hover:bg-amber-50 hover:border-amber-200"}`}>{n}</button>
                  ))}
                </div>
              </Field>

              {/* Slug */}
              <Field label="URL (slug)" hint="Boş bırakırsan başlıktan otomatik oluşur. Yayınlandıktan sonra değiştirme — eski bağlantılar kırılır.">
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-stone-400 font-mono">/calismalarim/</span>
                  <Input value={f.slug || ""} onChange={(e) => set("slug")(e.target.value)} placeholder="hukuki-danismanlik" data-testid="service-slug" className="font-mono" />
                </div>
              </Field>

              {/* Cover + focus */}
              <div className="border-t pt-6 space-y-4">
                <Field label="Kapak görseli" hint="Detay sayfasının en üstünde geniş olarak çıkar.">
                  <ImageUpload value={f.cover} onChange={set("cover")} testid="service-cover" alt={f.title?.tr} />
                </Field>

                {f.cover && (
                  <>
                    <Field label={`Kapak yüksekliği: ${f.hero_height || 0}px ${!f.hero_height ? "(0 = otomatik 21:9 oran)" : ""}`} hint="0 = otomatik oran. Değer verirsen o yükseklikte kırpılır (önerilen: 300–600).">
                      <input type="range" min="0" max="900" step="10" value={f.hero_height || 0} onChange={(e) => set("hero_height")(Number(e.target.value))} className="w-full accent-amber-600" data-testid="service-hero-height" />
                    </Field>
                    <Field label="Kapak odak noktası" hint="Resmin üzerinde tıklayıp sürükleyerek önemli yerin merkeze alınmasını sağla. Dar yüksekliklerde kırpma bu noktaya göre yapılır.">
                      <FocusPointPicker
                        src={f.cover}
                        value={{ x: f.cover_pos_x ?? 50, y: f.cover_pos_y ?? 50 }}
                        onChange={({ x, y }) => setF((prev) => ({ ...prev, cover_pos_x: x, cover_pos_y: y }))}
                        cropHeight={Math.max(140, Math.min(360, f.hero_height || 240))}
                        testid="service-cover-focus"
                      />
                    </Field>
                  </>
                )}
              </div>
            </div>
          </CollapsibleCard>

          {/* --- 4. Öne çıkanlar + CTA --- */}
          <CollapsibleCard num={4} title="Öne çıkan başlıklar ve çağrı butonu" subtitle='Sağ sidebar liste ve "Birlikte Çalışalım" butonu.' testid="section-highlights">
            <div className="space-y-5">
              <Field label="Öne çıkan başlıklar" hint="Detay sayfasının sağ sidebar'ında madde listesi olarak görünür.">
                <Highlights items={f.highlights} onChange={set("highlights")} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4 border-t pt-5">
                <Field label="Buton metni (TR / EN)"><I18nInput value={f.cta_label} onChange={set("cta_label")} placeholder="İletişime Geç" testid="service-cta-label" /></Field>
                <Field label="Buton bağlantısı"><Input value={f.cta_target} onChange={(e) => set("cta_target")(e.target.value)} placeholder="/iletisim" data-testid="service-cta-target" className="font-mono text-sm" /></Field>
              </div>
            </div>
          </CollapsibleCard>

          {/* --- 5. Firma bilgileri --- */}
          <CollapsibleCard num={5} title="Firma bilgileri" subtitle="Bu hizmeti hangi firma üzerinden veriyorsunuz? Detay sayfasında firma kartı olarak görünür." icon={<Building2 size={16} className="text-amber-700" />} testid="section-company">
            <CompanyEditor value={f.company} onChange={set("company")} />
          </CollapsibleCard>

          {/* --- 6. Sayfa düzeni (bloklar) --- */}
          <CollapsibleCard num={6} title="Sayfa düzeni (bloklar)" subtitle="Metin, liste, görsel, galeri, çalışma ve referans bloklarını istediğin sırada diz." testid="section-blocks">
            <BlockBuilder value={f.content_blocks || []} onChange={set("content_blocks")} parentCases={f.cases} parentRefs={f.testimonials} />
          </CollapsibleCard>

          {/* --- 7. Veri kaynakları: Çalışmalar & Referanslar --- */}
          <CollapsibleCard num={7} title="Çalışmalar (iş örnekleri)" subtitle='Yukarıda "Çalışma kartları grid" bloğunu eklediğinde buradaki çalışmalar grid olarak görünür.' testid="section-cases">
            <CaseEditor items={f.cases} onChange={set("cases")} />
          </CollapsibleCard>

          <CollapsibleCard num={8} title="Referanslar" subtitle='"Referans grid" bloğu eklediğinde burada eklediklerin alıntı kartları olarak çıkar.' testid="section-references">
            <TestimonialEditor items={f.testimonials} onChange={set("testimonials")} />
          </CollapsibleCard>
      </div>

      {/* Bottom sticky toolbar (mirrors the top one so uzun sayfalarda scroll etmeden kaydedilebilir) */}
      <Toolbar bottom />
    </div>
  );
}

// ----- Logo strip (trust bar) -----
function LogoStripManager() {
  const qc = useQueryClient();
  const { data: site } = useQuery({ queryKey: ["public", "site"], queryFn: () => api.get("/public/site").then((r) => r.data) });
  const strip = site?.services_strip || { enabled: false, title: { tr: "Birlikte çalıştığım kurumlar", en: "Trusted by" }, logos: [] };
  const [f, setF] = useState(strip);
  useEffect(() => { setF(strip); /* eslint-disable-next-line */ }, [site?.services_strip]);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const addLogo = () => set("logos")([...(f.logos || []), { name: "", image: "", url: "" }]);
  const updLogo = (i, patch) => { const nx = [...(f.logos || [])]; nx[i] = { ...nx[i], ...patch }; set("logos")(nx); };
  const delLogo = (i) => { const nx = [...(f.logos || [])]; nx.splice(i, 1); set("logos")(nx); };
  const save = async () => {
    try {
      await api.put("/admin/settings/services_strip", f);
      toast.success("Logo şeridi güncellendi");
      qc.invalidateQueries({ queryKey: ["public", "site"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-white border rounded-xl p-5 mt-10" data-testid="services-logo-strip">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-semibold text-base">Logo şeridi (opsiyonel)</h2>
          <p className="text-sm text-stone-500">Çalışmalarım sayfasının üstünde sessiz bir "güven şeridi" olarak çıkar. Çalıştığın kurumların logolarını yükle.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.enabled} onCheckedChange={set("enabled")} data-testid="logo-strip-toggle" />Göster</label>
          <Button type="button" onClick={save} size="sm" data-testid="logo-strip-save"><Save size={14} className="mr-1" />Kaydet</Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Field label="Başlık TR"><Input value={f.title?.tr || ""} onChange={(e) => set("title")({ ...(f.title || {}), tr: e.target.value })} placeholder="Birlikte çalıştığım kurumlar" data-testid="logo-strip-title-tr" /></Field>
        <Field label="Başlık EN"><Input value={f.title?.en || ""} onChange={(e) => set("title")({ ...(f.title || {}), en: e.target.value })} placeholder="Trusted by" data-testid="logo-strip-title-en" /></Field>
      </div>
      <div className="space-y-3">
        {(f.logos || []).map((l, i) => (
          <div key={i} className="border rounded-lg p-3 bg-stone-50 grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end" data-testid={`logo-strip-row-${i}`}>
            <Field label="Kurum adı"><Input value={l.name || ""} onChange={(e) => updLogo(i, { name: e.target.value })} placeholder="Örn. Baro" /></Field>
            <Field label="Opsiyonel bağlantı"><Input value={l.url || ""} onChange={(e) => updLogo(i, { url: e.target.value })} placeholder="https://…" /></Field>
            <div className="min-w-[160px]"><ImageUpload label="Logo" value={l.image} onChange={(v) => updLogo(i, { image: v })} testid={`logo-strip-img-${i}`} /></div>
            <Button type="button" variant="ghost" size="sm" onClick={() => delLogo(i)}><Trash2 size={14} className="text-red-600" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addLogo} data-testid="logo-strip-add"><Plus size={14} className="mr-1" />Logo ekle</Button>
      </div>
    </div>
  );
}

export default function CalismalarimAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data } = useQuery({ queryKey: ["admin", "services"], queryFn: () => api.get("/admin/services").then((r) => r.data) });
  const items = data?.items || [];

  const remove = async (s) => {
    if (!window.confirm(`"${s.title?.tr || s.slug}" hizmetini silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/admin/services/${s.id}`);
      toast.success("Silindi");
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const move = async (idx, dir) => {
    const n = [...items];
    const j = idx + dir;
    if (j < 0 || j >= n.length) return;
    [n[idx], n[j]] = [n[j], n[idx]];
    try {
      await api.post("/admin/services/reorder", { ids: n.map((x) => x.id) });
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageTitle title="Çalışmalarım" sub="Verdiğiniz hizmetleri, iş örneklerini ve referansları buradan yönetin. Site menüsündeki 'Çalışmalarım' sayfasında kart kart görünür." />
      {editing ? (
        <Editor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={(opts) => {
            qc.invalidateQueries({ queryKey: ["admin", "services"] });
            // Editor artık kaydetten sonra sayfada kalıyor; sadece explicit "stay=false" ise listeye dön.
            if (opts && opts.stay === false) setEditing(null);
          }}
        />
      ) : (
        <>
          {/* Onboarding banner */}
          <div className="bg-gradient-to-br from-amber-50 to-stone-50 border border-amber-200 rounded-xl p-5 mb-6" data-testid="services-onboarding">
            <h2 className="font-semibold text-base mb-2 flex items-center gap-2"><Briefcase size={16} className="text-amber-600" />Nasıl çalışır?</h2>
            <ol className="text-sm text-stone-700 space-y-1.5 list-decimal pl-5">
              <li><strong>"Yeni hizmet"</strong> butonuna basın (sağ üstte) ya da aşağıdaki örnekleri düzenleyin.</li>
              <li>Başlık + 1 cümlelik kısa açıklama + uzun açıklama yazın. İkon seçin (Lucide çipleri hazır).</li>
              <li>"Öne çıkan başlıklar" (sidebar listesi), "Çalışmalar" (iş örnekleri) ve "Referanslar" ekleyin — her biri bağımsız.</li>
              <li>Sol üstteki <strong>Yayında</strong> anahtarı kapalıysa sitede görünmez — taslak olarak tutar.</li>
            </ol>
            <p className="text-xs text-stone-500 mt-3">İpucu: Örnek 3 hizmet (Hukuki Danışmanlık, Eğitim ve Mentorluk, Sivil Toplum) sizin için önceden eklendi — "Düzenle"ye basıp kendi uzmanlık alanlarınızla değiştirebilirsiniz.</p>
          </div>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-stone-500">{items.length} hizmet</p>
            <div className="flex items-center gap-2">
              <a href="/calismalarim" target="_blank" rel="noopener noreferrer" className="text-sm flex items-center gap-1 text-stone-600 hover:text-stone-900"><ExternalLink size={14} />Sayfayı aç</a>
              <Button onClick={() => setEditing(EMPTY())} data-testid="service-new" size="lg" className="shadow-sm"><Plus size={16} className="mr-1.5" />Yeni hizmet</Button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-stone-300 rounded-xl p-12 text-center" data-testid="services-empty">
              <Briefcase size={40} className="mx-auto mb-4 text-stone-300" />
              <h3 className="font-semibold text-lg mb-2">Henüz hizmet eklemediniz</h3>
              <p className="text-sm text-stone-500 mb-5 max-w-md mx-auto">Çalışmalarım sayfası boş görünecek. İlk hizmetinizi ekleyin — sonrasında istediğiniz zaman çalışma örneği ve referans ekleyebilirsiniz.</p>
              <Button onClick={() => setEditing(EMPTY())} size="lg"><Plus size={16} className="mr-1.5" />İlk hizmeti ekle</Button>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="services-list">
              {items.map((s, i) => (
                <li key={s.id} className="bg-white border rounded-xl p-4 flex items-center gap-3 hover:border-s-primary/40 transition-colors" data-testid={`service-row-${s.slug}`}>
                  <div className="flex flex-col gap-0.5">
                    <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={13} /></Button>
                    <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, 1)} disabled={i === items.length - 1}><ArrowDown size={13} /></Button>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0"><Briefcase size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{s.title?.tr || s.slug}</p>
                    <p className="text-xs text-stone-500 truncate">{s.short?.tr || "— kısa açıklama yok —"}</p>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-stone-500">
                      <span className="font-mono text-stone-400">/{s.slug}</span>
                      <span>{(s.cases || []).length} çalışma</span>
                      <span>{(s.testimonials || []).length} referans</span>
                      {!s.is_published && <span className="text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Taslak</span>}
                    </div>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setEditing(s)} data-testid={`service-edit-${s.slug}`}>Düzenle</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => remove(s)}><Trash2 size={14} className="text-red-600" /></Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <LogoStripManager />
    </>
  );
}
